import type { ReactNode } from 'react'
import { Platform, useWindowDimensions, View } from 'react-native'
import { BREAKPOINTS } from '@/lib/config'
import { Button, type ButtonProps } from './Button'
import { ButtonLink, type ButtonLinkProps } from './Link'

type HeaderButtonType = 'edit' | 'save' | 'create' | 'delete' | 'create-save' | 'export'

const TYPE_DEFAULTS: Record<HeaderButtonType, { title: string; icon?: ButtonProps['icon'] }> = {
    edit: { title: 'Edit', icon: 'cog' },
    save: { title: 'Save', icon: 'content-save' },
    create: { title: 'Create', icon: 'plus' },
    delete: { title: 'Delete', icon: 'delete' },
    'create-save': { title: 'Save', icon: 'content-save-plus' },
    export: { title: 'Export', icon: 'download' },
}

const headerStyle = Platform.OS === 'ios' ? undefined : { marginRight: 8 }

type HeaderButtonLinkProps = Omit<ButtonLinkProps, 'title'> & {
    type: HeaderButtonType
    title?: string
}

export function HeaderButtonLink({ type, title, ...props }: HeaderButtonLinkProps) {
    const defaults = TYPE_DEFAULTS[type]
    return (
        <ButtonLink
            title={title ?? defaults.title}
            icon={defaults.icon}
            mode="contained"
            {...props}
            style={[headerStyle, props.style]}
        />
    )
}

type HeaderButtonProps = Omit<ButtonProps, 'title'> & {
    type: HeaderButtonType
    title?: string
}

const ICON_ONLY_ON_SMALL_SCREEN: Set<HeaderButtonType> = new Set(['delete'])

export function HeaderButton({ type, title, ...props }: HeaderButtonProps) {
    const defaults = TYPE_DEFAULTS[type]
    const { width } = useWindowDimensions()
    const isSmallScreen = width < BREAKPOINTS.md
    const iconOnly = isSmallScreen && ICON_ONLY_ON_SMALL_SCREEN.has(type)

    return (
        <Button
            title={iconOnly ? '' : (title ?? defaults.title)}
            icon={defaults.icon}
            {...props}
            style={[headerStyle, props.style]}
        />
    )
}

export function HeaderButtonGroup({ children }: { children: ReactNode }) {
    return <View style={{ flexDirection: 'row', gap: 12 }}>{children}</View>
}
